import { useQuery } from "convex/react";
import { Link } from "react-router-dom";
import { Calendar, Clock, MapPin, Video, ChevronDown, ExternalLink } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { api } from "@/convex";
import { generateGoogleCalendarLink, generateOutlookCalendarLink } from "@/lib/calendar-utils";
import type { CalendarEvent } from "@/lib/calendar-utils";

function getEventThumbnail(title: string): string | null {
  if (title.toLowerCase().includes("energy freedom")) return "/events/energy-freedom.jpg";
  if (title.toLowerCase().includes("dc dinner")) return "/events/dc-dinner.jpg";
  return null;
}

const Events = () => {
  const events = useQuery(api.events.list, {});

  return (
    <div className="min-h-screen">
      <div className="container mx-auto px-4 sm:px-6 py-10 max-w-4xl">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl sm:text-4xl font-semibold text-foreground mb-2 tracking-tight">
            Upcoming Events
          </h1>
          <p className="text-muted-foreground text-sm sm:text-base tracking-wide">
            Here are all the upcoming events, calls, dinners, and more.
          </p>
        </div>

        {/* Loading State */}
        {events === undefined && (
          <div className="space-y-6">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="bg-card border-border/40">
                <CardContent className="p-5 space-y-3">
                  <Skeleton className="h-6 w-3/4" />
                  <div className="flex gap-2">
                    <Skeleton className="h-5 w-24" />
                    <Skeleton className="h-5 w-20" />
                  </div>
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-2/3" />
                  <Skeleton className="h-9 w-32" />
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Empty State */}
        {events && events.length === 0 && (
          <Card className="bg-card border-border/40">
            <CardContent className="p-8 text-center">
              <Calendar className="w-10 h-10 text-muted-foreground/50 mx-auto mb-3" />
              <p className="text-muted-foreground">No upcoming events scheduled.</p>
            </CardContent>
          </Card>
        )}

        {/* Events List */}
        {events && events.length > 0 && (
          <div className="space-y-6">
            {events.map((event) => (
              <Card
                key={event._id}
                data-testid="event-card"
                className="bg-card border-border/40 overflow-hidden"
              >
                {(event.imageUrl || getEventThumbnail(event.title)) && (
                  <img
                    src={(event.imageUrl || getEventThumbnail(event.title))!}
                    alt={event.title}
                    className="w-full h-40 object-cover sm:hidden"
                  />
                )}
                <CardContent className="p-4 sm:p-5">
                  <div className="flex gap-4">
                    <div className="flex-1 min-w-0" data-testid="event-card-copy">
                      {/* Title */}
                      <h3 className="text-lg font-semibold text-foreground mb-3">
                        {event.title}
                      </h3>

                      {/* Badges */}
                      <div className="flex items-center gap-1.5 mb-3 flex-wrap">
                        <Badge variant="outline" className="font-medium text-xs px-2 py-0.5">
                          {event.type}
                        </Badge>
                        {event.status === "RSVP Required" && (
                          <Badge variant="outline" className="font-medium text-xs px-2 py-0.5">
                            RSVP Required
                          </Badge>
                        )}
                        {event.dateTbd && !event.hideTbdBadge && (
                          <Badge variant="outline" className="font-medium text-xs px-2 py-0.5">
                            Date TBD
                          </Badge>
                        )}
                        {event.tierRequired && (
                          <Badge className="bg-primary text-primary-foreground text-xs px-2 py-0.5">
                            {event.tierRequired}
                          </Badge>
                        )}
                      </div>

                      {/* Event Details */}
                      <div className="space-y-2 mb-4">
                        <div className="flex items-center text-sm text-muted-foreground">
                          <Calendar className="w-4 h-4 mr-3 text-muted-foreground/70" />
                          <span>{event.dateDisplay}</span>
                        </div>
                        {!event.dateTbd && (
                          <div className="flex items-center text-sm text-muted-foreground">
                            <Clock className="w-4 h-4 mr-3 text-muted-foreground/70" />
                            <span>{event.time}</span>
                          </div>
                        )}
                        <div className="flex items-center text-sm text-muted-foreground">
                          {event.zoomLink ? (
                            <Video
                              aria-label="Online event"
                              data-testid="online-event-icon"
                              className="w-4 h-4 mr-3 text-muted-foreground/70"
                            />
                          ) : (
                            <MapPin
                              aria-label="Physical location"
                              data-testid="physical-location-icon"
                              className="w-4 h-4 mr-3 text-muted-foreground/70"
                            />
                          )}
                          {event.zoomLink ? (
                            <span>
                              {event.location} | Meeting ID: {event.meetingId}, Passcode: {event.passcode}
                            </span>
                          ) : (
                            <span>{event.location}</span>
                          )}
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="grid grid-cols-2 sm:flex sm:items-center gap-2 sm:gap-3">
                        {event.zoomLink && (
                          <Button size="sm" className="col-span-2 sm:w-auto" asChild>
                            <a href={event.zoomLink} target="_blank" rel="noopener noreferrer">
                              Join Zoom Meeting
                            </a>
                          </Button>
                        )}
                        {!event.dateTbd && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="outline" size="sm" className="w-full sm:w-auto">
                                Add to Calendar
                                <ChevronDown className="w-3 h-3 ml-1" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent>
                              <DropdownMenuItem asChild>
                                <a
                                  href={generateGoogleCalendarLink(event)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="cursor-pointer"
                                >
                                  Google Calendar
                                </a>
                              </DropdownMenuItem>
                              <DropdownMenuItem asChild>
                                <a
                                  href={generateOutlookCalendarLink(event)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="cursor-pointer"
                                >
                                  Outlook / Office 365
                                </a>
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                        {!event.zoomLink && event.type === "In-Person" && (
                          <a href="mailto:michelle@alexepstein.com" className="text-sm text-primary hover:underline">
                            Contact the ETP team to learn more about attending
                          </a>
                        )}
                        {event.type === "Annual Conference" && event.title.includes("Energy Freedom 2026") && (
                          <Button variant="outline" size="sm" className="w-full sm:w-auto" asChild>
                            <Link to="/conference">
                              View Conference Details
                            </Link>
                          </Button>
                        )}
                      </div>
                    </div>

                    {/* Thumbnail — desktop only (mobile shows banner above) */}
                    {(event.imageUrl || getEventThumbnail(event.title)) && (
                      <div className="hidden sm:flex flex-shrink-0 items-center">
                        <img
                          src={(event.imageUrl || getEventThumbnail(event.title))!}
                          alt={event.title}
                          data-testid="event-card-photo-desktop"
                          className="w-48 aspect-[5/4] rounded-lg object-cover"
                        />
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Events;
